// pilot_mode: owner-side pilot banner and practice/live switch (removable with the pilot feature)
import { useState } from "react";
import { toast } from "sonner";
import { FlaskConical, Radio, CreditCard, HandCoins } from "lucide-react";
import { Input } from "@/components/ui/input";
import { errMsg } from "@/lib/api";

export const PilotBanner = ({ pilot }) => {
  if (!pilot?.notice) return null;
  const urgent = pilot.days_left != null && !pilot.waived;
  return (
    <div className={`mt-4 rounded-2xl p-4 font-semibold ${urgent ? "bg-ready-pale" : "bg-brand-light text-ink"}`} data-testid="pilot-banner">
      {pilot.notice}{pilot.waived && pilot.end_date ? ` Pilot runs until ${pilot.end_date}.` : ""}
    </div>
  );
};

const Option = ({ on, onClick, icon: I, title, text, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`flex-1 rounded-2xl border-2 p-4 text-left transition-colors ${on ? "border-brand bg-brand-pale" : "border-line hover:border-brand-mid"}`}>
    <div className="flex items-center gap-2 font-heading font-extrabold"><I size={18} /> {title}{on && <span className="ml-auto rounded-full bg-brand px-2 text-xs text-white">Active</span>}</div>
    <p className="mt-1 text-xs text-muted2">{text}</p>
  </button>
);

export const PaymentMethodCard = ({ api, venue, reload }) => {
  const [mins, setMins] = useState(venue.approval_timeout_minutes || 10);
  if (!venue.pilot?.is_pilot) return null;
  const save = async (method) => {
    try { await api.put(`/venues/${venue.id}/payment-method`, { method, approval_timeout_minutes: Number(mins) || 10 }); toast.success("Payment method saved"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="card-lg p-5" data-testid="payment-method-card">
      <h3 className="text-lg font-extrabold">Payment method <span className="ml-1 rounded-full bg-brand-light px-2 text-xs">Pilot</span></h3>
      <p className="text-xs text-muted2">Applies in Live mode.</p>
      <div className="mt-3 flex flex-col gap-3 sm:flex-row">
        <Option on={venue.payment_method !== "manual"} onClick={() => save("stripe")} icon={CreditCard} testid="payment-method-stripe-btn"
          title="Stripe" text="Customers pay by card, Apple Pay or Google Pay. Needs Stripe Connect." />
        <Option on={venue.payment_method === "manual"} onClick={() => save("manual")} icon={HandCoins} testid="payment-method-manual-btn"
          title="Manual approval, pay at pickup" text="No Stripe needed. Staff approve each order; customers pay cash, card terminal or e-Transfer at pickup." />
      </div>
      <label className="mt-3 flex items-center gap-2 text-sm">Auto-expire unapproved orders after
        <Input className="h-8 w-16" inputMode="numeric" value={mins} onChange={(e) => setMins(e.target.value.replace(/\D/g, ""))} onBlur={() => venue.payment_method === "manual" && save("manual")} data-testid="approval-timeout-input" /> minutes</label>
      {venue.no_show_count > 0 && <p className="mt-2 text-xs text-muted2" data-testid="no-show-count">No-shows so far: {venue.no_show_count}</p>}
    </div>
  );
};

export const OrderModeCard = ({ api, venue, reload }) => {
  const setMode = async (mode) => {
    try { await api.put(`/venues/${venue.id}/order-mode`, { mode }); toast.success(mode === "practice" ? "Practice mode on" : "Live mode on"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="card-lg p-5" data-testid="order-mode-card">
      <h3 className="text-lg font-extrabold">Order mode</h3>
      <div className="mt-3 flex flex-col gap-3 sm:flex-row">
        <Option on={venue.order_mode === "practice"} onClick={() => setMode("practice")} icon={FlaskConical} testid="order-mode-practice-btn"
          title="Practice" text="Stripe test cards only, no real money. Labelled PRACTICE everywhere. Never counts toward fees or reports." />
        <Option on={venue.order_mode === "live"} onClick={() => setMode("live")} icon={Radio} testid="order-mode-live-btn"
          title="Live" text="Real orders through your connected Stripe account. GST and LineGone commission apply." />
      </div>
    </div>
  );
};
